/**
 * Permission injection seam for shared pages.
 *
 * Shared pages call `usePermissions().hasPermission('roles.edit')` without knowing
 * how each app computes it. Each app mounts <PermissionsProvider> inside its own auth
 * scope and feeds its `hasPermission` (cloud: from AuthContext; local: from Redux via
 * useAuth). Mirrors how <ApiProvider> injects the API.
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react';

export interface Permissions {
  hasPermission: (permission: string) => boolean;
  /** True if the user has AT LEAST ONE of the given permissions (OR). */
  hasAny: (...permissions: string[]) => boolean;
  /** True if the user has ALL of the given permissions (AND). */
  hasAll: (...permissions: string[]) => boolean;
  /**
   * True when the current user holds the super_admin role. Capability checks
   * (hasPermission) can't express this — super_admin and admin both pass
   * `roles.edit` — so shared pages that need to distinguish the two (e.g. only
   * super_admin may edit the super_admin role) read this flag.
   */
  isSuperAdmin: boolean;
}

const PermissionsContext = createContext<Permissions | null>(null);

export function PermissionsProvider({
  hasPermission,
  isSuperAdmin = false,
  children,
}: {
  hasPermission: (permission: string) => boolean;
  isSuperAdmin?: boolean;
  children: ReactNode;
}) {
  // Derive hasAny/hasAll from the single injected hasPermission so apps only
  // ever need to supply one function (super_admin bypass lives inside it).
  const value = useMemo<Permissions>(
    () => ({
      hasPermission,
      hasAny: (...permissions: string[]) => permissions.some((p) => hasPermission(p)),
      hasAll: (...permissions: string[]) => permissions.every((p) => hasPermission(p)),
      isSuperAdmin,
    }),
    [hasPermission, isSuperAdmin]
  );
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

/** Access injected permissions. Throws if used outside <PermissionsProvider>. */
export function usePermissions(): Permissions {
  const ctx = useContext(PermissionsContext);
  if (!ctx) {
    throw new Error('usePermissions() must be used within a <PermissionsProvider>.');
  }
  return ctx;
}
